import { useState, useEffect, useCallback } from 'react';

import { apiClient, CREDITS_CHANGED_EVENT } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useProfile, AccountType } from '../contexts/ProfileContext';

export type ActionType =
  'simulation_official' | 'correction' | 'exam_creation' | 'student_correction';

export type Bucket = 'subscription' | 'topup' | 'promo';

export type BucketCounts = Record<ActionType, number>;

export interface CreditsBalance {
  accountType: AccountType;
  actionTypes: ActionType[];
  buckets: { subscription: BucketCounts; topup: BucketCounts; promo: BucketCounts };
  totals: BucketCounts;
  updatedAt: string | null;
}

export interface UseCreditsReturn {
  balance: CreditsBalance | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Decide se vale buscar `/me/credits`. O motor de créditos só serve o B2B
 * (professor/empresarial) desde a migração para o plano único: o aluno B2C tem
 * correção e chat ilimitados, então o saldo dele não gateia nada e o fetch a
 * cada navegação seria puro desperdício.
 *
 * Consequência desejada em cascata: com `balance === null`, todas as
 * superfícies de crédito (badge, banner de saldo baixo, painel, `requireCredits`)
 * degradam para "escondido / não bloqueia" sem precisar de gate próprio.
 */
export function shouldFetchCredits(accountType: AccountType): boolean {
  return accountType === 'business';
}

/**
 * Hook de leitura dos créditos do usuário autenticado (Fase 6).
 *
 * - Faz fetch de `/me/credits` ao montar, apenas para contas `business`.
 * - Re-fetch automático quando `accountType` muda (após troca em
 *   `AccountTypeSwitcher`), porque a lista de `actionTypes` relevante é
 *   derivada disso no backend.
 * - Sem cache global (D1 do plano): cada consumidor monta o próprio estado.
 *   A chance de inconsistência é zero porque ambos chamam o mesmo endpoint.
 *
 * Para forçar refresh após uma operação que consome crédito, o componente
 * chama `refetch()` explicitamente.
 */
export function useCredits(): UseCreditsReturn {
  const { user } = useAuth();
  const { accountType } = useProfile();
  const [balance, setBalance] = useState<CreditsBalance | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const enabled = shouldFetchCredits(accountType);

  const refetch = useCallback(async () => {
    if (!user || !enabled) {
      setBalance(null);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient('/me/credits');
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const data = (await res.json()) as CreditsBalance;
      setBalance(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao carregar créditos');
      setBalance(null);
    } finally {
      setLoading(false);
    }
  }, [user, enabled]);

  useEffect(() => {
    refetch();
    // Re-fetch quando accountType muda — a lista de actionTypes relevantes
    // é resolvida pelo backend a partir do profile.accountType.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, accountType]);

  // Re-fetch quando uma operação sinaliza mudança de saldo (ex.: correção
  // concluída debita 1 crédito no fim). Desacoplado via evento global, no
  // mesmo padrão de INSUFFICIENT_CREDITS_EVENT.
  useEffect(() => {
    if (!user || !enabled) return;
    const handler = () => {
      refetch();
    };
    window.addEventListener(CREDITS_CHANGED_EVENT, handler);
    return () => window.removeEventListener(CREDITS_CHANGED_EVENT, handler);
  }, [user, enabled, refetch]);

  return { balance, loading, error, refetch };
}
